import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PayerStrip from "../components/PayerStrip";
import {
  HeartHandshake, Home, Users, Clock, Car, Phone, ClipboardList, ShieldCheck, HandCoins,
} from "lucide-react";
import { growthApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const SERVICES = [
  { icon: HeartHandshake, name: "Homemaker Personal Care (HPC)", text: "Bathing, dressing, grooming and mobility support under DODD waivers." },
  { icon: Home, name: "Homemaker Services", text: "Light housekeeping, laundry and meal preparation." },
  { icon: Users, name: "Companionship", text: "Friendly visits, conversation and activity engagement." },
  { icon: Clock, name: "Respite Care", text: "Scheduled relief for family caregivers." },
  { icon: Car, name: "Transportation Support", text: "Errands, appointments and community outings." },
];

const CountyPage = () => {
  const { slug } = useParams();
  const [county, setCounty] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCounty(null);
    setNotFound(false);
    growthApi.publicCounty(slug)
      .then((r) => {
        setCounty(r.data);
        track("county_page_visit", r.data.name);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const name = county?.name || "";
  useSeo({
    title: name ? `Home Care & DODD Services in ${name} County, Ohio | Caregivers Alliance Homecare` : "Ohio Home Care | Caregivers Alliance Homecare",
    description: name ? `Home care in ${name} County, Ohio: personal care, homemaker services, companionship, respite & transportation support. DODD, MyCare, PASSPORT & private pay inquiries welcome. Call (614) 526-1548.` : "",
    keywords: name ? `home care ${name} County Ohio, DODD provider ${name} County, HPC provider ${name} County Ohio, respite care ${name} County, non medical transportation ${name} County, private pay home care ${name} County` : "",
    path: `/ohio/${slug}/home-care`,
    robots: county?.pageApproved ? "index,follow" : "noindex,nofollow",
  });

  if (notFound) {
    return (
      <section className="section bg-white min-h-[50vh]">
        <div className="mx-auto max-w-3xl px-4 text-center py-20">
          <h1 className="font-display font-bold text-3xl text-[#0E2A33]">County page not found</h1>
          <p className="text-slate-500 mt-4">We serve all 88 Ohio counties. Call (614) 526-1548 or contact us about availability in your area.</p>
          <Link to="/contact"><Button className="mt-6 rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-8">Contact Us</Button></Link>
        </div>
      </section>
    );
  }

  if (!county) return <div className="min-h-[50vh] grid place-items-center text-slate-400">Loading…</div>;

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <div className="text-white/80 text-sm mb-2">Ohio → {name} County</div>
          <h1 data-testid="county-h1" className="font-display font-extrabold text-4xl md:text-5xl">
            Home Care & DODD Services in {name} County, Ohio
          </h1>
          <p className="mt-4 text-white/90 max-w-3xl">
            Caregivers Alliance Homecare works with individuals, families, referral professionals, and
            county-board teams seeking qualified home-care services in {name} County.
          </p>
          <div data-testid="county-availability" className="mt-5 inline-flex items-center gap-2 bg-white/15 rounded-full px-5 py-2 text-sm font-medium">
            <ShieldCheck className="h-4 w-4" /> {county.availabilityStatus}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact"><Button data-testid="county-refer-btn" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-7 font-semibold">REFER A CLIENT</Button></Link>
            <Link to="/contact"><Button data-testid="county-request-btn" className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-7 font-semibold">REQUEST HOME CARE</Button></Link>
            <Link to="/family-caregiver"><Button data-testid="county-fc-btn" className="rounded-full bg-white/15 hover:bg-white/25 text-white h-12 px-7 font-semibold border border-white/30">ASK ABOUT FAMILY CAREGIVER OPTIONS</Button></Link>
          </div>
        </div>
      </section>

      {county.intro && (
        <section className="section bg-white">
          <div className="mx-auto max-w-4xl px-4">
            <p data-testid="county-intro" className="text-slate-600 text-lg leading-relaxed">{county.intro}</p>
          </div>
        </section>
      )}

      <section className={`section ${county.intro ? "bg-[#F7FBFC]" : "bg-white"}`}>
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33] text-center">Services Available in {name} County</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center">
                  <div className="brand-gradient h-11 w-11 rounded-xl grid place-items-center text-white mx-auto mb-3"><Icon className="h-5 w-5" /></div>
                  <div className="font-semibold text-[#0E2A33]">{s.name}</div>
                  <p className="text-xs text-slate-500 mt-2">{s.text}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Service availability, authorization and program participation vary by plan and must be verified before services begin.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid md:grid-cols-3 gap-6">
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-7">
            <ClipboardList className="h-6 w-6 text-[#0E5A72] mb-3" />
            <h3 className="font-display font-bold text-lg text-[#0E2A33]">How to Refer a Client</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Hospital case managers, discharge planners, SSAs and county-board teams can call{" "}
              <a href="tel:6145261548" className="text-[#0E5A72] font-medium">(614) 526-1548</a> or use our online
              referral form. We respond quickly so care is in place when it's needed.
            </p>
            <Link to="/contact" className="text-[#0E5A72] text-sm font-medium hover:underline mt-3 inline-block">Refer a patient →</Link>
          </div>
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-7">
            <ShieldCheck className="h-6 w-6 text-[#0E5A72] mb-3" />
            <h3 className="font-display font-bold text-lg text-[#0E2A33]">DODD / County Board Information</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              {county.boardName
                ? `Individuals with developmental disabilities in ${name} County are typically supported through the ${county.boardName}. We welcome referral inquiries involving county-board service coordinators — contact us to discuss the individual's service plan and authorization.`
                : `Individuals with developmental disabilities in ${name} County are typically supported through their local County Board of Developmental Disabilities. Contact us to discuss service availability for a specific individual.`}
            </p>
          </div>
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-7">
            <HandCoins className="h-6 w-6 text-[#0E5A72] mb-3" />
            <h3 className="font-display font-bold text-lg text-[#0E2A33]">Private Pay & Family Caregiver Pathway</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Private-pay home care offers flexible scheduling with no waiver required. And if you already have a trusted
              family member or friend helping with care, some Ohio participant-directed programs may allow a qualified
              person to provide approved services — eligibility depends on the waiver, service plan and program rules.
            </p>
            <Link to="/family-caregiver" className="text-[#0E5A72] text-sm font-medium hover:underline mt-3 inline-block">Family caregiver options →</Link>
          </div>
        </div>
      </section>

      {county.faq?.length > 0 && (
        <section className="section bg-[#F7FBFC]">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33] text-center">
              {name} County Home Care — Frequently Asked Questions
            </h2>
            <div className="mt-8 space-y-4">
              {county.faq.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <div className="font-semibold text-[#0E2A33]">{f.q}</div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <PayerStrip compact />

      <section className="section brand-gradient text-white">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="font-display font-bold text-3xl">Contact Caregivers Alliance</h2>
          <p className="mt-3 text-white/90">
            6161 Busch Blvd, Suite 119, Columbus, OH 43229 · {name} County is part of our Ohio service area.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="tel:6145261548" onClick={() => track("call_click", name)}>
              <Button className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-8 font-semibold"><Phone className="mr-2 h-4 w-4" /> (614) 526-1548</Button>
            </a>
            <Link to="/contact"><Button className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-8 font-semibold">Request Home Care</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CountyPage;
